import React, { useState, useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useAuth } from '../context/AuthContext';

const fireConfetti = () => {
  const defaults = {
    zIndex: 99999,
    colors: ['#c9a227', '#FFD700', '#0a1628', '#0a7e8c', '#ffffff', '#ff6b6b'],
    disableForReducedMotion: true
  };

  // Big initial burst from center
  confetti({
    ...defaults,
    particleCount: 150,
    spread: 100,
    startVelocity: 55,
    origin: { x: 0.5, y: 0.4 },
    scalar: 1.2,
    gravity: 0.8,
    ticks: 300
  });

  // Left cannon
  setTimeout(() => confetti({
    ...defaults,
    particleCount: 80,
    angle: 60,
    spread: 60,
    startVelocity: 50,
    origin: { x: 0, y: 0.65 },
    scalar: 1.3,
    ticks: 300
  }), 200);

  // Right cannon
  setTimeout(() => confetti({
    ...defaults,
    particleCount: 80,
    angle: 120,
    spread: 60,
    startVelocity: 50,
    origin: { x: 1, y: 0.65 },
    scalar: 1.3,
    ticks: 300
  }), 200);

  // Continuous rain from both sides
  const end = Date.now() + 4000;
  const frame = () => {
    confetti({
      ...defaults,
      particleCount: 4,
      angle: 60,
      spread: 40,
      startVelocity: 40,
      origin: { x: 0, y: Math.random() * 0.4 + 0.3 },
      scalar: 1.1,
      ticks: 200
    });
    confetti({
      ...defaults,
      particleCount: 4,
      angle: 120,
      spread: 40,
      startVelocity: 40,
      origin: { x: 1, y: Math.random() * 0.4 + 0.3 },
      scalar: 1.1,
      ticks: 200
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // Second burst at 1.5s
  setTimeout(() => confetti({
    ...defaults,
    particleCount: 100,
    spread: 120,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.5 },
    scalar: 1.4,
    ticks: 250
  }), 1500);

  // Final shower at 3s
  setTimeout(() => confetti({
    ...defaults,
    particleCount: 60,
    spread: 160,
    startVelocity: 30,
    origin: { x: 0.5, y: 0 },
    scalar: 1.5,
    gravity: 1.2,
    ticks: 400
  }), 3000);
};

const WinnerOverlay = () => {
  const { user } = useAuth();
  const [winData, setWinData] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setWinData(null);
  }, []);

  useEffect(() => {
    if (!user) return;

    const handler = (e) => {
      const data = e.detail;
      if (data.winner_id === user.user_id) {
        setWinData({
          horseName: data.horse_name,
          amount: data.winning_amount
        });
      }
    };

    window.addEventListener('auction-winner', handler);
    return () => window.removeEventListener('auction-winner', handler);
  }, [user]);

  // Fire confetti + auto-dismiss
  useEffect(() => {
    if (!winData) return;

    fireConfetti();

    timerRef.current = setTimeout(dismiss, 7000);
    return () => { clearTimeout(timerRef.current); };
  }, [winData, dismiss]);

  if (!winData) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 cursor-pointer"
      onClick={dismiss}
      data-testid="winner-overlay"
    >
      <div className="text-center px-6 animate-scaleIn">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#c9a227] flex items-center justify-center shadow-lg shadow-[#c9a227]/40">
          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
          Complimenti!
        </h2>
        <p className="text-xl md:text-2xl text-white/90 font-medium mb-2">
          Hai vinto l'asta!
        </p>
        {winData.horseName && (
          <p className="text-lg md:text-xl text-[#c9a227] font-bold mt-4">
            {winData.horseName}
          </p>
        )}
        {winData.amount > 0 && (
          <p className="text-white/70 text-base mt-2">
            Prezzo finale: <span className="text-white font-bold">{'\u20AC'}{winData.amount.toLocaleString()}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default WinnerOverlay;
